import { UsedCreditInvoiceSummary } from "@/features/invoices/credit-invoices/credit-invoice.type";
import { LineSheetSetType } from "@/features/line-sheet-sets/line-sheet-set.types";
import { Company, Price, StoredObject } from "@/features/types";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import { Formik } from "formik";
import { DEFAULT_SEASON } from "@/features/line-sheet-sets/app-season-select";
import { Grid, Heading } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppListHeader from "@/features/ui/app-list-header";
import React, { useCallback, useMemo, useRef } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { CreditInvoice } from "@/features/invoices/credit-invoices/credit-invoice.type";
import {
  AbstractInvoice,
  OrderConfirmationInvoice
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useIdentity from "@/features/ui/hooks/use-identity";
import {
  useProformaInvoiceDumbTransaction
} from "@/features/invoices/proforma-invoices/hooks/use-proforma-invoice-dumb-transaction";
import { DenseOrderSheetSet, LightOrderSheetSet } from "@/features/order-sheets/order-sheet.type";
import useProformaInvoiceFormValidationSchema
  from "@/features/invoices/proforma-invoices/hooks/use-proforma-invoice-form-validation-schema";
import useGetMySelf from "@/features/auth/use-get-my-self";
import { PriceInputValue } from "@/features/invoices/app-price-input";
import PriceUtils from "@/features/ui/utils/price-utils";
import ProformaInvoiceFormModal from "@/features/invoices/proforma-invoices/proforma-invoice-form-modal";
import { useRecoilValue } from "recoil";
import { exchangeCurrencyStore } from "@/shared/store/exchangeCurrencyStore";
import {
  CROSS_TRADER_REQUIRED_MESSAGE,
  isCrossTraderMissing,
  proformaRequiredAmounts,
  resolveTotalRequiredAmount,
} from "@/features/invoices/proforma-invoices/cross-trade-fee";

export interface ProformaInvoiceFormFieldsModal {
  // 금액 통화 — 없으면 유로(라인시트세트가 원화면 원화)
  currency?: Price["currency"];
  id?: number;
  number?: string;
  name?: string;
  season?: string;
  status?: AbstractInvoice["status"];
  lineSheetSetType?: LineSheetSetType;
  orderedBy?: Pick<Company, "id" | "name">;
  issuedBy?: Company;
  issuedThrough?: Pick<Company, "id" | "name">;
  originallyIssuedBy?: Company;
  issuedOn: Date;
  exposedToBuyer: boolean;
  paidByAgency: boolean;
  publicComment?: string;
  privateComment?: string;
  // orderSheetSets: LinkedOrderSheetSet[];
  orderSheetSets: LightOrderSheetSet[];
  orderConfirmationInvoices: OrderConfirmationInvoice[];
  expectedRemainingDepositAmount?: PriceInputValue;
  paymentMethod?: ProformaInvoice["paymentMethod"];

  isRemittedToBoutique?: boolean;
  isRemittedToAgency?: boolean;
  usedDeposit?: PriceInputValue;
  usedDepositComment?: string;
  usedCreditInvoices: UsedCreditInvoiceSummary[];
  usedCreditComment?: string;
  // 타부티크(다른 부티크 발행) 바이어 크레딧도 선택지에 보여줄지. 기본은 이 프로포르마 부티크 발행 크레딧만.
  usesOtherBoutiqueCredit?: boolean;
  exchangeRate?: number;
  bankFee?: PriceInputValue
  unoFeeRate?: number;
  unoFeeExchangeRate?: number;
  isBuyerToAgencyEur?: boolean;
  // 3자무역 여부(화면에서만 쓰고 저장하지 않는다 — 발행사가 3자무역 회사면 3자무역)
  isCrossTrade?: boolean;
  // 3자무역 수수료율(%) — 송금 필요 금액에 곱한다. 3자무역이 아니면 비운다.
  crossTradeFeeRate?: number;
  // 인보이스 총액 — 담당자가 인보이스 금액을 그대로 넣는다(브랜드별 금액은 서버가 비율로 나눈다)
  totalAmount?: PriceInputValue;
  // 총 송금 필요 금액 직접 입력값 — 비어 있으면 자동 계산값을 쓴다(인보이스 금액에 맞출 때만 채운다)
  totalRequiredAmount?: PriceInputValue;

  isDetailsEditedManually: boolean;
  proformaInvoiceDetailList: (Omit<
    ProformaInvoice["proformaInvoiceDetailList"][number],
    | "orderSheetSet"
    | "orderConfirmationInvoiceDetail"
    | "brand"
    | "amount"
    | "usedDepositAmount"
  > & {
    orderSheetSet?: {
      id: number;
      lineSheetSet?: {
        id: number;
        name: string;
      };
    };

    brand?: {
      id: number;
      name: string;
    };

    amount?: PriceInputValue;
    usedDepositAmount?: PriceInputValue;
    usedCreditAmount?: PriceInputValue;
    requiredAmount?: PriceInputValue;

    //errorMessage 생성시에 name 연산하기 위해 oc 정보 추가함
    orderConfirmationInvoiceDetail?: ProformaInvoice["proformaInvoiceDetailList"][number]["orderConfirmationInvoiceDetail"] & {
      orderConfirmationInvoice: {
        id: number;
        name: string;
      };
    };
  })[];
  file?: File | Pick<StoredObject, "id" | "name">;
  childProformaInvoices: {
    id: number;
    orderConfirmationInvoices: { id: number }[];
    orderSheetSets: { id: number }[];
  }[];
  parentProformaInvoices: { id: number }[];
  earnedCreditInvoices: { id: number }[];
  payments: {
    id: number;
    status: AbstractInvoice["status"];
    sender: Pick<Company, "id" | "name">;
  }[];
  attachments: (File | Pick<StoredObject, "id" | "name">)[];
}

export default function UploadProformaInvoicePageModal(
  {
    buyerId,
    boutiqueId,
    orderSheetSet,
    currency: lineSheetSetCurrency,
    isSeason = false,
    season,
    handleClose
  }: {
    buyerId: number;
    boutiqueId: number;
    orderSheetSet: LightOrderSheetSet;
    // 라인시트세트 통화 — 프로포마 금액을 이 통화로 올린다(없으면 유로)
    currency?: string | null;
    // 시즌 프로포마 — 오더 대신 OC(오더 컨펌 인보이스)를 연결하고 디파짓을 차감한다
    isSeason?: boolean;
    // 라인시트세트 시즌 — 시즌 프로포마는 이 시즌의 OC를 연결한다
    season?: string;
    handleClose: () => void;
  }
) {
  const { error: showError, warning: showWarning } = useAppToasts();
  const identity = useIdentity();
  const { tSentence, tTitle } = useI18nHelper();
  const { navigate } = useRouterHelper();

  // 원화 라인시트(국내 부티크)는 원화로 올린다. 그 외는 지금처럼 유로다.
  const currency: Price["currency"] =
    lineSheetSetCurrency === "KRW" ? "KRW" : "EUR";
  const exchangeCurrencyInfo = useRecoilValue(exchangeCurrencyStore);
  const initialExchangeRateRef = useRef(
    // 원화 프로포마는 환전이 없어 환율 1
    currency === "KRW"
      ? 1
      : exchangeCurrencyInfo["EUR"]?.remittance_send_rate !== undefined
      ? Math.round(exchangeCurrencyInfo["EUR"].remittance_send_rate)
      : undefined
  );


  const validateSchema = useProformaInvoiceFormValidationSchema();

  const validate = useCallback(
    async (values: ProformaInvoiceFormFieldsModal) => {
      try {
        await validateSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
      if (isCrossTraderMissing(values)) {
        showWarning(CROSS_TRADER_REQUIRED_MESSAGE);
        return { issuedBy: CROSS_TRADER_REQUIRED_MESSAGE };
      }
    },
    [validateSchema, showError, showWarning]
  );

  const { addCreate } = useProformaInvoiceDumbTransaction();

  const handleSubmit = useCallback(
    async (values: ProformaInvoiceFormFieldsModal) => {
      if (identity?.company) {
        // 브랜드별 금액은 보내지 않는다 — 서버가 총액·사용 디파짓을 연결 오더·OC 비율로 나눠 저장한다(2026-10-30)
        const { amount: totalAmount, requiredAmount } = proformaRequiredAmounts(
          values,
          currency
        );
        const usedCredit = PriceUtils.sum(
          values.usedCreditInvoices.map(
            (creditInvoice) => creditInvoice.amount
          ),
          currency
        );

        await addCreate(
          {
            by: identity.company.type,
            companyId: identity.company.id,
          },
          {
            name: values.name!,
            number: values.number!,
            status: values.status!,
            lineSheetSetType: values.lineSheetSetType!,
            season: values.season!,
            issuedByCompany: values.issuedBy!,
            issuedToCompany: values.orderedBy!,
            originallyIssuedByCompany: values.originallyIssuedBy!,
            issuedThroughCompany: values.issuedThrough || null,
            issuedOn: values.issuedOn,
            paidBy: values.paidByAgency ? "AGENCY" : "BUYER",
            isBuyerToAgencyEur: values.paidByAgency && !!values.isBuyerToAgencyEur,
            totalAmount: PriceUtils.placeholder(totalAmount, currency) as Price,
            totalRequiredAmount: PriceUtils.placeholder(
              resolveTotalRequiredAmount(values.totalRequiredAmount, requiredAmount),
              currency
            ),
            // 3자무역 수수료율은 더 이상 쓰지 않는다(총액에 인보이스 금액을 그대로 넣는다)
            crossTradeFeeRate: null,
            paymentMethod: values.paymentMethod!,
            isPublic: values.exposedToBuyer,
            publicComment: values.publicComment || null,
            privateComment: values.privateComment || null,
            usedDeposit: PriceUtils.placeholder(values.usedDeposit, currency),
            usedDepositComment: values.usedDepositComment || null,
            usedCredit: PriceUtils.placeholder(usedCredit, currency),
            usedCreditComment: values.usedCreditComment || null,
            usedCreditInvoices: values.usedCreditInvoices.map(
              (creditInvoice) => {
                return {
                  id: creditInvoice.id,
                };
              }
            ),
            orderSheetSets: values.orderSheetSets.map((orderSheetSet) => {
              return {
                id: orderSheetSet.id,
              };
            }),
            orderConfirmationInvoices: values.orderConfirmationInvoices.map(
              (ocInvoice) => {
                return {
                  id: ocInvoice.id,
                };
              }
            ),
            proformaInvoiceDetailList: [],
            file: values.file!,
            isDetailsEditedManually: false,
            childProformaInvoices: values.childProformaInvoices.map(
              (pInvoice) => {
                return {
                  id: pInvoice.id,
                };
              }
            ),
            attachments: values.attachments,
            exchangeRate: values.exchangeRate || 1,
            bankFee: PriceUtils.placeholder(values.bankFee, "KRW"),
            unoFeeRate:
              values.unoFeeRate !== undefined && values.unoFeeRate !== null
                ? values.unoFeeRate / 100
                : null,
            unoFeeExchangeRate: values.unoFeeExchangeRate ?? (values.exchangeRate || 1),
          }
        );
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 500);
      });
    },
    [addCreate, identity, currency]
  );

  const { data: mySelf, error: getMySelfError } = useGetMySelf();


  const initialValues = useMemo((): ProformaInvoiceFormFieldsModal => {
    return {
      id: -1,
      // 시즌은 연결할 OC를 시즌으로 찾으므로 라인시트세트 시즌을 기본값으로 둔다
      season: (isSeason && season) || DEFAULT_SEASON,
      lineSheetSetType: "SEASON",
      status: "NORMAL",
      issuedOn: (() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
      })(),
      exposedToBuyer: true,
      paidByAgency: true,
      // 시즌은 오더를 연결하지 않고 OC를 고른다
      orderSheetSets: isSeason ? [] : [orderSheetSet],
      orderConfirmationInvoices: [], //
      usedCreditInvoices: [],
      isDetailsEditedManually: false,
      issuedBy: {
        id: boutiqueId,
        name: '',
        type: "BOUTIQUE"
      },
      proformaInvoiceDetailList: [
        // todo너도 왜 -1을 하는거야?
        // {
        //   id: -1,
        // },
      ],
      paymentMethod: "NORMAL",
      childProformaInvoices: [],
      parentProformaInvoices: [],
      earnedCreditInvoices: [],
      payments: [],
      orderedBy: {
        id: buyerId,
        name: ''
      },
      issuedThrough: {
        id: 1,
        name: ""
      },
      attachments: [],
      exchangeRate: initialExchangeRateRef.current,
      currency,
      bankFee: PriceUtils.placeholder(
        {
          currency: "KRW",
          // 원화 프로포마는 국내 송금이라 해외 송금 수수료가 없다
          value: currency === "KRW" ? 0 : 43000,
        },
        "KRW"
      ),
    };
  }, [boutiqueId, buyerId, orderSheetSet, currency, isSeason, season]);

  return (
    <Formik<ProformaInvoiceFormFieldsModal>
      enableReinitialize={true}
      initialValues={initialValues}
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      onSubmit={(values) => {
        return handleSubmit(values);
      }}
    >
      {(props) => {
        return (
          <Grid
            as={"form"}
            height={"100%"}
            width={"100%"}
            gridTemplateColumns={"1fr"}
            gridTemplateRows={"auto auto 1fr"}
          >
            <AppListHeader
              headers={[
                <Heading
                  key={"UploadOrderConfirmationInvoice"}
                  as={"h2"}
                  fontSize={"14px"}
                >
                  {tTitle("proforma_invoices:upload_proforma_invoice")}
                </Heading>,
              ]}
              filters={[]}
              actions={[
                <AppButton
                  key={`Cancel`}
                  isDisabled={props.isSubmitting}
                  onClick={async () => {
                    handleClose();
                  }}
                >
                  {tTitle("cancel")}
                </AppButton>,
                <AppButton
                  key={`Upload`}
                  variant={"primary"}
                  isLoading={props.isSubmitting}
                  onClick={async () => {
                    const result = await props.submitForm();
                    // @ts-ignore
                    if (result) {
                      handleClose();
                      // navigate("/proforma-invoices");
                    }
                  }}
                >
                  {tTitle("upload")}
                </AppButton>,
              ]}
              showNavigateToBack={false}
              emptyStackPage={"/proforma-invoices"}
              blockNavigateToBack={props.isSubmitting || props.dirty}
            />
            <ProformaInvoiceFormModal
              formik={props}
              isEditing={true}
              isSeason={isSeason}
            />
          </Grid>
        );
      }}
    </Formik>
  );
}
